import React, { useState, useEffect } from 'react';
import Papa from 'papaparse'; // Add this import for CSV parsing

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export default function CompetitorManagement() {
    const [competitors, setCompetitors] = useState([]);
    const [events, setEvents] = useState([]);
    const [selectedEventID, setSelectedEventID] = useState('');
    const [newCompetitor, setNewCompetitor] = useState({ CompetitorID: '', FirstName: '', LastName: '', Organization: '', CompetitorState: '', Division: '' });
    const [sortBy, setSortBy] = useState(null);
    const [csvFile, setCsvFile] = useState(null); // Add this state for CSV file
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/getAllEvents`);
                const data = await response.json();
                const enabledEvents = data.filter(event => event.Enabled);
                setEvents(enabledEvents);
                if (enabledEvents.length > 0) {
                    setSelectedEventID(enabledEvents[0].EventID);
                    fetchCompetitors(enabledEvents[0].EventID);
                }
            } catch (error) {
                console.error('Error fetching events:', error);
            }
        };

        fetchEvents();
    }, []);

    const fetchCompetitors = async (eventID) => {
        console.log(`Fetching competitors for EventID: ${eventID}`);
        try {
            const response = await fetch(`${API_BASE_URL}/getAllCompetitors?EventID=${eventID}`);
            const data = await response.json();
            console.log('Fetched Competitors:', data); // Log the data for inspection
            setCompetitors(Array.isArray(data) ? data : []); // Ensure data is an array
        } catch (error) {
            console.error('Error fetching competitors:', error);
        }
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setNewCompetitor({ CompetitorID: '', FirstName: '', LastName: '', Organization: '', CompetitorState: '', Division: '' });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewCompetitor(prev => ({ ...prev, [name]: value }));
    };

    const handleAddOrUpdateCompetitor = async () => {
        if (!newCompetitor.CompetitorID || !newCompetitor.FirstName || !newCompetitor.LastName || !newCompetitor.Organization || !newCompetitor.CompetitorState || !newCompetitor.Division) {
            alert('All fields are required!');
            return;
        }

        const competitorData = { ...newCompetitor, EventID: selectedEventID }; // Include EventID
        const existingCompetitor = competitors.find(comp => comp.CompetitorID === newCompetitor.CompetitorID && comp.EventID === selectedEventID);
        const method = existingCompetitor ? 'PUT' : 'POST';
        const endpoint = existingCompetitor ? `${API_BASE_URL}/updateCompetitor` : `${API_BASE_URL}/createCompetitor`;

        try {
            const response = await fetch(endpoint, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(competitorData)
            });

            if (response.ok) {
                fetchCompetitors(selectedEventID);
                closeModal();
            } else {
                const errorData = await response.json();
                alert(`Error: ${errorData.message || 'Failed to save competitor.'}`);
            }
        } catch (error) {
            console.error('Error saving competitor:', error);
            alert('An error occurred while saving the competitor.');
        }
    };

    const handleDeleteCompetitor = async (id) => {
        const confirmDelete = window.confirm('!!!WARNING!!! Deleting a competitor will also remove them from any Teams and remove all of their scores for this event. Are you SURE you want to delete this competitor?');
        if (!confirmDelete) return;

        try {
            const response = await fetch(`${API_BASE_URL}/deleteCompetitor?CompetitorID=${id}&EventID=${selectedEventID}`, { method: 'DELETE' }); // Include EventID
            if (response.ok) {
                alert('Competitor deleted successfully.');
                setCompetitors(competitors.filter(comp => comp.CompetitorID !== id));
            } else {
                const errorData = await response.json();
                alert(`Error: ${errorData.message || 'Failed to delete competitor.'}`);
            }
        } catch (error) {
            console.error('Error deleting competitor:', error);
        }
    };

    const handleEditCompetitor = (competitor) => {
        setNewCompetitor(competitor);
        openModal();
    };

    const handleCancel = () => {
        closeModal();
    };

    const handleSort = (column) => {
        const sortedData = [...competitors].sort((a, b) => {
            if (a[column] < b[column]) return -1;
            if (a[column] > b[column]) return 1;
            return 0;
        });
        setCompetitors(sortedData);
        setSortBy(column);
    };

    const handleFileChange = (e) => {
        setCsvFile(e.target.files[0]);
    };

    const handleUploadCSV = async () => {
        if (!csvFile) {
            alert('Please select a CSV file to upload.');
            return;
        }
    
        // Fetch existing competitors before processing the CSV
        await fetchCompetitors(selectedEventID).then(() => {
            Papa.parse(csvFile, {
                header: true,
                complete: async (results) => {
                    const competitorsData = results.data
                        .map(comp => ({ ...comp, EventID: selectedEventID }))
                        .filter(comp => comp.CompetitorID); // Filter out empty rows
    
                    for (const competitor of competitorsData) {
                        const existingCompetitor = competitors.find(comp => comp.CompetitorID === competitor.CompetitorID && comp.EventID === selectedEventID);
                        const endpoint = existingCompetitor ? `${API_BASE_URL}/updateCompetitor` : `${API_BASE_URL}/createCompetitor`;
                        const method = existingCompetitor ? 'PUT' : 'POST';
    
                        try {
                            const response = await fetch(endpoint, {
                                method,
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(competitor)
                            });
    
                            if (!response.ok) {
                                const errorData = await response.json();
                                alert(`Error: ${errorData.message || 'Failed to upload competitor.'}`);
                                return;
                            }
                        } catch (error) {
                            console.error('Error uploading competitor:', error);
                            alert('An error occurred while uploading the competitors.');
                            return;
                        }
                    }
                    fetchCompetitors(selectedEventID);
                    alert('Competitors uploaded successfully.');
                },
                error: (error) => {
                    console.error('Error parsing CSV:', error);
                    alert('An error occurred while parsing the CSV file.');
                }
            });
        });
    };

    const getHighlightClass = (competitor) => {
        const duplicates = competitors.filter(
            (comp) =>
                comp.FirstName === competitor.FirstName &&
                comp.LastName === competitor.LastName &&
                comp.Organization === competitor.Organization &&
                comp.CompetitorState === competitor.CompetitorState &&
                comp.Division === competitor.Division
        );
    
        const sameOrganization = competitors.filter(
            (comp) => comp.Organization === competitor.Organization
        );
    
        if (duplicates.length > 1) {
            return { className: 'highlight-duplicate', tooltip: 'Potential duplicate competitor: First Name, Last Name, Organization, State, and Division match another competitor.' };
        } else if (sameOrganization.length === 1) {
            return { className: 'highlight-unique', tooltip: 'This is the only competitor with this Organization: Confirm if the Organization is correct or if this is the only competitor from this organization.' };
        }
        return { className: '', tooltip: '' };
    };

    return (
        <div className="p-1">
            <div className="flex mb-1">
                <label className="form-control-inline">
                    <select className="form-select" value={selectedEventID || ''} onChange={(e) => {
                        setSelectedEventID(Number(e.target.value));
                        fetchCompetitors(Number(e.target.value)); // Fetch competitors when an event is selected
                    }}>
                        <option value="">Select Event</option>
                        {events.map(event => (
                            <option key={event.EventID} value={event.EventID}>{event.EventName}</option>
                        ))}
                    </select>
                </label>
            </div>

            <button className="btn btn-primary mb-1" onClick={openModal}>
                Add Competitor
            </button>

            <div className="card mb-1">
                <h3>Upload Competitors CSV</h3>
                <input type="file" accept=".csv" onChange={handleFileChange} className="form-control" />
                <div className="mt-1">
                    <button className="btn btn-primary" onClick={handleUploadCSV}>Upload CSV</button>
                </div>
            </div>

            {competitors.length > 0 && (
                <div className="data-table-wrapper">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('CompetitorID')}>ID</th>
                                <th onClick={() => handleSort('FirstName')}>First Name</th>
                                <th onClick={() => handleSort('LastName')}>Last Name</th>
                                <th onClick={() => handleSort('Organization')}>Organization</th>
                                <th onClick={() => handleSort('State')}>State</th>
                                <th onClick={() => handleSort('Division')}>Division</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {competitors.map((competitor) => {
                                const { className, tooltip } = getHighlightClass(competitor);
                                return (
                                    <tr key={competitor.CompetitorID} className={className} title={tooltip}>
                                        <td>{competitor.CompetitorID}</td>
                                        <td>{competitor.FirstName}</td>
                                        <td>{competitor.LastName}</td>
                                        <td>{competitor.Organization}</td>
                                        <td>{competitor.CompetitorState}</td>
                                        <td>{competitor.Division}</td>
                                        <td>
                                            <div className="flex gap-1">
                                                <button className="btn btn-primary" onClick={() => handleEditCompetitor(competitor)}>Edit</button>
                                                <button className="btn btn-danger" onClick={() => handleDeleteCompetitor(competitor.CompetitorID)}>Delete</button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {isModalOpen && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h3>{newCompetitor.CompetitorID ? 'Edit Competitor' : 'Add New Competitor'}</h3>
                        <input  
                            className="form-control"
                            type="text"
                            name="CompetitorID"
                            placeholder="Competitor ID (required)"
                            value={newCompetitor.CompetitorID}
                            onChange={handleInputChange}
                            required
                        />
                        <input
                            className="form-control"
                            type="text"
                            name="FirstName"
                            placeholder="First Name (required)"
                            value={newCompetitor.FirstName}
                            onChange={handleInputChange}
                            required
                        />
                        <input
                            className="form-control"
                            type="text"
                            name="LastName"
                            placeholder="Last Name (required)"
                            value={newCompetitor.LastName}
                            onChange={handleInputChange}
                            required
                        />
                        <input
                            className="form-control"
                            type="text"
                            name="Organization"
                            placeholder="Organization (required)"
                            value={newCompetitor.Organization}
                            onChange={handleInputChange}
                            required
                        />
                        <input
                            className="form-control"
                            type="text"
                            name="CompetitorState"
                            placeholder="State (required)"
                            value={newCompetitor.CompetitorState}
                            onChange={handleInputChange}
                            required
                        />
                        <select
                            className="form-select"
                            name="Division"
                            value={newCompetitor.Division}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="">Select Division (required)</option>
                            <option value="Men">Men</option>
                            <option value="Women">Women</option>
                        </select>
                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleAddOrUpdateCompetitor}>
                                Save Competitor
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
